import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import dayjs from "dayjs";
import { api } from "../../../singleton/axios";
import {
  SubscriptionEntity,
  findSubscriptionForEntity,
  levelLabelForTopic,
  subscribeErrorMessage,
} from "../subscriptionSelectors";
import { MySubscriptions, NotificationSubscription, SubscriptionLevel } from "../types";
import { MY_SUBSCRIPTIONS_QUERY_KEY } from "./useMySubscriptions";

interface SubscribeVariables extends SubscriptionEntity {
  level: SubscriptionLevel;
  /** Display name for the optimistic row only (the server stores its own). */
  label?: string | null;
}

interface SubscribeResponse {
  subscription: NotificationSubscription;
}

/**
 * Turn alerts on, or change the level of alerts that are already on — the
 * server treats both as one idempotent POST and answers with the ONE
 * subscription the user holds for that company, whichever ticker it was
 * created through. Optimistic against the shared subscriptions list; no
 * success toast (the control itself changes), a readable snackbar on failure.
 */
export const useSubscribe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // `entityRef` and `label` shape the optimistic row and are never sent: the
    // server resolves the identity itself, from the ticker that was pressed.
    mutationFn: async ({ topic, entityKind, entityKey, level }: SubscribeVariables) => {
      const { data } = await api.post<SubscribeResponse>("/notification-subscriptions", {
        topic,
        entityKind,
        entityKey,
        level,
      });
      return data.subscription;
    },

    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: MY_SUBSCRIPTIONS_QUERY_KEY });
      const previous = queryClient.getQueryData<MySubscriptions>(MY_SUBSCRIPTIONS_QUERY_KEY);
      const levelLabel = levelLabelForTopic(variables.topic, variables.level);

      queryClient.setQueryData<MySubscriptions>(MY_SUBSCRIPTIONS_QUERY_KEY, (old) => {
        if (!old) return old;
        const existing = findSubscriptionForEntity(old.subscriptions, variables);
        if (existing) {
          return {
            ...old,
            subscriptions: old.subscriptions.map((subscription) =>
              subscription.id === existing.id
                ? { ...subscription, level: variables.level, levelLabel }
                : subscription,
            ),
          };
        }
        const optimistic: NotificationSubscription = {
          id: `temp-${dayjs().valueOf()}`,
          topic: variables.topic,
          topicLabel: "",
          entityKind: variables.entityKind,
          entityKey: variables.entityKey,
          entityRef: variables.entityRef,
          label: variables.label ?? variables.entityKey,
          level: variables.level,
          levelLabel,
          path: null,
          imageKind: variables.entityKind === "ticker" ? "symbol" : "icon",
          imageValue: variables.entityKind === "ticker" ? variables.entityKey : "bell",
          createdAt: dayjs().toISOString(),
        };
        return { ...old, subscriptions: [optimistic, ...old.subscriptions] };
      });

      return { previous };
    },

    // Swap the optimistic row for the server's at once, so its real id is
    // there for "Turn off" without waiting on the refetch.
    onSuccess: (subscription, variables) => {
      queryClient.setQueryData<MySubscriptions>(MY_SUBSCRIPTIONS_QUERY_KEY, (old) => {
        if (!old) return old;
        const placeholder = findSubscriptionForEntity(old.subscriptions, variables);
        if (!placeholder) {
          const others = old.subscriptions.filter((item) => item.id !== subscription.id);
          return { ...old, subscriptions: [subscription, ...others] };
        }
        return {
          ...old,
          subscriptions: old.subscriptions.map((item) =>
            item.id === placeholder.id ? subscription : item,
          ),
        };
      });
    },

    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(MY_SUBSCRIPTIONS_QUERY_KEY, context.previous);
      }
      const code = isAxiosError(error) ? error.response?.data?.error : undefined;
      enqueueSnackbar(subscribeErrorMessage(code), { variant: "error", autoHideDuration: 5000 });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_SUBSCRIPTIONS_QUERY_KEY });
    },
  });
};
